import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { getAssigneeListDetails, getUsersByIds } from '../../../../repository';
import { buildExpressQuery } from '../../../../repository/queries/builders';
import getUserGroupsByIds from '../../../../repository/queries/getUserGroupsByIds';
import useExpress from '../../../../repository/react-query/useExpress';
import { PAY_FILTER } from './constants';
import { toSentenceCase, toTitleCase } from '../../../../../utils';

const getPaymentLineItemConfigs = (startDate, endDate, rateId, paymentType) => {
    const params = {};
    params.filter = {
        __t: { $ne: 'Loan' },
    };

    // Apply date filtering if startDate and endDate are provided
    if (startDate && endDate) {
        const start = moment(startDate).startOf('day').toISOString();
        const end = moment(endDate).endOf('day').toISOString();
        params.filter = {
            start_date: { $lte: end },
            $or: [{ end_date: { $gte: start } }, { end_date: { $eq: null } }],
            ...params.filter,
        };
    }

    // Apply rate filtering if rateId is provided
    if (rateId) {
        params.filter.payment_rate = rateId;
    }

    if (paymentType) {
        params.filter.payment_type = paymentType;
    }

    const endpoint = '/payment-line-item-config';
    const request = useExpress({ endpoint, params });

    /**
     * This gets the payer and payee information based on Users, Assignees and User groups.
     * This is needed because the payer_filter.ids references to both UserModel and AssigneeModel therefore using promise.all to individually fetch instead of mongoose populate as the result is getting overridden.
     */
    return async function () {
        const paymentLineItemConfigs = await request();
        const { userIds, userGroupIds, assigneeIds } = paymentLineItemConfigs.reduce(
            (acc, config) => {
                if (config?.payee_filter?.role === 'assignee') {
                    (config.payee_filter?.ids ?? []).forEach((id) => acc.assigneeIds.add(id));
                } else {
                    (config?.payee_filter?.ids ?? []).forEach((id) => acc.userIds.add(id));
                    (config?.payee_filter?.user_group ?? []).forEach((id) => acc.userGroupIds.add(id));
                }
                (config.payer_filter?.ids ?? []).forEach((id) => acc.userIds.add(id));
                (config.payer_filter?.user_group ?? []).forEach((id) => acc.userGroupIds.add(id));
                return acc;
            },
            { userIds: new Set(), userGroupIds: new Set(), assigneeIds: new Set() }
        );

        // May need to update all of these to body-query versions if id list becomes too large
        const [users, { assignees }, userGroups] = await Promise.all([getUsersByIds([...userIds], ['name'], buildExpressQuery), getAssigneeListDetails({ ids: [...assigneeIds], fields: ['assignee_name'] }), getUserGroupsByIds({ ids: [...userGroupIds] })]);

        const payerAndPayees = [...(users ?? []), ...(assignees ?? []), ...(userGroups ?? [])];
        const payerPayeesMap = new Map(payerAndPayees.map((entity) => [entity._id.toString(), entity]));

        const res = paymentLineItemConfigs.map(({ payee_filter, payer_filter, ...rest }) => {
            const payers = new Map();
            const payees = new Map();

            const addValueToSet = (value, map, filterType) => {
                const existingSet = map.get(filterType) || new Set();
                if (Array.isArray(value)) {
                    value.forEach((item) => existingSet.add(item));
                } else {
                    existingSet.add(value);
                }
                map.set(filterType, existingSet);
            };

            const addEntityNameToSet = (id, map, filterType = PAY_FILTER.USER_IDS) => {
                const entity = payerPayeesMap.get(id.toString());
                if (entity) {
                    const name = entity.name?.trim() ?? entity.assignee_name?.trim();
                    if (name) {
                        addValueToSet(name, map, filterType);
                    }
                }
            };

            // Machine-generated filter values (milk_category, pay_group, federal_order, state)
            // are snake_case/lowercase enums, so they are case-transformed for display.
            // User-entered names (users, assignees, user groups) are stored as-is.
            const toDisplayValue = (value) => toTitleCase(toSentenceCase(String(value)));

            const addNames = (filter, map) => {
                if (filter.ids) {
                    filter.ids.forEach((id) => addEntityNameToSet(id, map, PAY_FILTER.USER_IDS));
                }

                if (filter.user_group) {
                    filter.user_group.forEach((id) => addEntityNameToSet(id, map, PAY_FILTER.USER_GROUP));
                }

                Object.values(PAY_FILTER)
                    .filter((filterType) => filterType !== PAY_FILTER.USER_IDS && filterType !== PAY_FILTER.USER_GROUP && filter[filterType]) // Skip USER_IDS and USER_GROUP as they're handled separately
                    .forEach((filterType) => {
                        const raw = filter[filterType];
                        addValueToSet(Array.isArray(raw) ? raw.map(toDisplayValue) : toDisplayValue(raw), map, filterType);
                    });

                if (map.size === 0 && filter.role) {
                    // Synthesized display value (not a real name), so it is pre-cased here.
                    addValueToSet(toTitleCase(toSentenceCase(`${filter.role}s`)), map, 'All');
                }
            };

            addNames(payee_filter ?? [], payees);
            addNames(payer_filter ?? [], payers);

            // Structured sections ({ label, names }) so consumers can count names exactly
            // without re-splitting a display string (names may contain commas).
            // Values are display-ready by the time they reach the sets: user-entered
            // names are raw, machine enum values and the section label are pre-cased.
            const formatNameSections = (map) => {
                const sections = [];
                // eslint-disable-next-line no-restricted-syntax
                for (const [key, value] of map) {
                    if (value.size > 0) {
                        const label = key === PAY_FILTER.USER_IDS ? '' : `${toTitleCase(toSentenceCase(key))}: `;
                        sections.push({
                            label,
                            names: [...value],
                        });
                    }
                }

                return sections;
            };

            const formatNames = (sections, separator) => sections.map(({ label, names }) => `${label}${names.join(', ')}`).join(separator);

            const payerSections = formatNameSections(payers);
            const payeeSections = formatNameSections(payees);

            return {
                ...rest,
                payee_filter,
                payer_filter,
                payerNames: payerSections,
                payeeNames: payeeSections,
                payerName: formatNames(payerSections, '\n\n'),
                payeeName: formatNames(payeeSections, '\n\n'),
                payerNameCsv: formatNames(payerSections, '; '),
                payeeNameCsv: formatNames(payeeSections, '; '),
            };
        });
        return res;
    };
};

export const usePaymentLineItemConfigs = ({ startDate, endDate, rateId, paymentType, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemConfigs(startDate, endDate, rateId, paymentType);
    const queryKey = ['payment-line-item-configs', startDate, endDate, rateId, paymentType];
    return useQuery({ queryKey, queryFn, enabled });
};

export const getPaymentLineItemConfig = (configId) => {
    const endpoint = `/payment-line-item-config/${configId}`;
    return useExpress({ endpoint });
};

export const usePaymentLineItemConfig = ({ configId, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemConfig(configId);
    const queryKey = ['payment-line-item-config', configId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};

export const getPaymentLineItemRate = (rateId) => {
    const endpoint = '/payment-line-item-rate';
    const params = {
        where: {
            id: rateId,
        },
    };
    return useExpress({ endpoint, params });
};

export const usePaymentLineItemRate = ({ rateId, enabled = true } = {}) => {
    const queryFn = getPaymentLineItemRate(rateId);
    const queryKey = ['payment-line-item-rate', rateId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};
